import { DataRequestOption, SortColumn, SortOrder } from '@app/infrastructure/data-list/grid/api-grid/api';
import { IFilters } from '@app/infrastructure/data-list/grid/api-grid-filters/filters';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '@app/infrastructure/data-list/pagination/pagination.model';
import {
	KeyValueSelection,
	MultipleKeyValueSelection,
	RangeSelection,
} from '@app/infrastructure/filters/filters.model';
import { isNullish } from '@utils/is-nullish.tool';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';

export function isMultipleKeyValueSelection(filter: unknown): filter is MultipleKeyValueSelection {
	return Array.isArray(filter) && 'key' in (filter[0] || {}) && 'value' in (filter[0] || {});
}

export function isKeyValueSelection(filter: unknown): filter is KeyValueSelection {
	return typeof filter === 'object' && 'key' in (filter || {}) && 'value' in (filter || {});
}

export function isRangeSelection(filter: unknown): filter is RangeSelection {
	return typeof filter === 'object' && 'start' in (filter || {}) && 'end' in (filter || {});
}

export function isOneRangeProvided(rangeValue: RangeSelection): boolean {
	return (!!rangeValue.start && !rangeValue.end) || (!rangeValue.start && !!rangeValue.end);
}

export function isKeyORValueProvided(keyValue: KeyValueSelection): boolean {
	return (!!keyValue.key && !keyValue.value) || (!keyValue.key && !!keyValue.value);
}

export interface InitialState<FilterType> {
	page?: number | null;
	limit?: number | null;
	filters?: Partial<FilterType>;
	sortColumn?: SortColumn;
}

export class ApiGridFilters<FilterType> implements IFilters<FilterType> {
	protected store$ = new BehaviorSubject<DataRequestOption<FilterType>>(null);

	public static create<FilterType>(initialState?: InitialState<FilterType>): () => ApiGridFilters<FilterType> {
		return () => new ApiGridFilters<FilterType>(initialState);
	}

	constructor(private initialState: InitialState<FilterType> = {}) {
		this.applyInitState();
	}

	public getDefaultFilters(): FilterType {
		return this.initialState?.filters as FilterType;
	}

	public changes(): Observable<DataRequestOption<FilterType>> {
		return this.get();
	}

	public get(): Observable<DataRequestOption<FilterType>> {
		return this.store$.pipe(
			filter((store) => !!store),
			map((store) => {
				const normalizedFilters = this.removeNullAndUndefined(store.filters);

				return {
					...store,
					filters: normalizedFilters,
				};
			}),
		);
	}

	public hasApplied(): Observable<boolean> {
		return this.get().pipe(
			map(({ filters }) => !!this.getKeysNotDefaultFilters(filters).length),
			share(),
		);
	}

	public refresh(): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
		});
	}

	public navigateTo(page: number, limit: number): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
			page,
			limit,
		});
	}

	public decreasePage(): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
			page: currentState.page !== null && currentState.page > 1 ? currentState.page - 1 : currentState.page,
		});
	}

	public increasePage(): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
			page: currentState.page !== null ? currentState.page + 1 : currentState.page,
		});
	}

	public sort(columnName: string, sortOrder: SortOrder): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
			sortColumn: { columnName, sortOrder },
		});
	}

	public update(value: Partial<FilterType>): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
			limit: this.initialState.limit || DEFAULT_PAGE_SIZE,
			page: FIRST_PAGE,
			filters: { ...currentState.filters, ...this.prepareFilters(value) },
		});
	}

	public reset(): void {
		this.applyInitState();
	}

	public updateCurrentState(): void {
		const currentState = this.store$.getValue();
		this.store$.next({
			...currentState,
		});
	}

	public updateInitState(state: InitialState<FilterType>): void {
		this.initialState = { ...this.initialState, ...state };
	}

	public get state(): InitialState<FilterType> {
		return this.store$.value;
	}

	/**
	 * If filter is complex (object or array like: MultipleSelection, RangeSelection, KeyValueSelection,
	 * MultipleKeyValueSelection, SelectionWithLabel, MultipleSelectionWithLabel) AND on of the values is null
	 * => Then fill it accordingly.
	 * @param filters
	 * @private
	 */
	private prepareFilters(filters: Partial<FilterType>): Partial<FilterType> {
		return Object.entries(filters).reduce((accum, [key, value]: [string, unknown]) => {
			if (isRangeSelection(value) && isOneRangeProvided(value)) {
				return { ...accum, [key]: { start: value.start || 0, end: value.end || 'Infinity' } };
			}
			if (isKeyValueSelection(value) && isKeyORValueProvided(value)) {
				return { ...accum, [key]: { key: value.key || '', value: value.value || '' } };
			}
			if (isMultipleKeyValueSelection(value)) {
				const normalizedKeyValue = value.map((item) => {
					if (isKeyORValueProvided(item)) {
						return { key: item.key || '', value: item.value || '' };
					}
					return item;
				});
				const normalizedKeyValueWithoutNullish = normalizedKeyValue.filter((filterObj) =>
					Object.values(filterObj).every((filterObjValue) => !isNullish(filterObjValue)),
				);
				return { ...accum, [key]: normalizedKeyValueWithoutNullish };
			}

			return { ...accum, [key]: value };
		}, {});
	}

	private applyInitState(): void {
		const filters = (this.initialState?.filters ? this.initialState.filters : {}) as FilterType;

		this.store$.next({
			page: this.initialState?.page ?? FIRST_PAGE,
			limit: this.initialState?.limit ?? DEFAULT_PAGE_SIZE,
			filters,
			...(this.initialState?.sortColumn && {
				sortColumn: {
					columnName: this.initialState.sortColumn.columnName,
					sortOrder: this.initialState.sortColumn.sortOrder,
				},
			}),
		});
	}

	private removeNullAndUndefined(filters: Partial<FilterType>): FilterType {
		return Object.entries(filters).reduce((accum, [key, value]: [string, unknown]) => {
			if (isNullish(value)) {
				return { ...accum };
			}
			if (
				typeof value === 'object' &&
				(value.hasOwnProperty('start') || value.hasOwnProperty('end')) &&
				isNullish(value['start']) &&
				isNullish(value['end'])
			) {
				return { ...accum };
			}
			if (
				typeof value === 'object' &&
				(value.hasOwnProperty('key') || value.hasOwnProperty('value')) &&
				isNullish(value['key']) &&
				isNullish(value['value'])
			) {
				return { ...accum };
			}
			if (Array.isArray(value)) {
				// all object inside the array are nullish > skip that array
				// Example: [ { key: null, value: null } ]
				if (value.every((filterObj) => Object.values(filterObj).every((filterObjValue) => isNullish(filterObjValue)))) {
					return { ...accum };
				}
				// skip objects inside the value with all nullish values
				// Example: [ { key: 'test', value: 'test' }, { key: null, value: null } ] => [ { key: 'test', value: 'test' } ]
				const normalizedValue = value.filter((filterObj) =>
					Object.values(filterObj).every((filterObjValue) => !isNullish(filterObjValue)),
				);

				return { ...accum, [key]: normalizedValue };
			}

			return { ...accum, [key]: value };
		}, {} as FilterType);
	}

	private getKeysNotDefaultFilters(filters: FilterType): string[] {
		const keysFilters: string[] = Object.keys(filters);
		const keysDefaultFilters: string[] = this.getDefaultFilters() ? Object.keys(this.getDefaultFilters()) : [];

		return keysDefaultFilters.length ? keysFilters.filter((key) => keysDefaultFilters.indexOf(key) < 0) : keysFilters;
	}
}
